import React, { useState, useEffect, useCallback, useRef } from 'react';
import useAdminInstance from '../hooks/useAdminInstance';
import styled from '@emotion/styled';
import { RefreshCw, ChevronDown, ChevronRight, Clock, Zap, AlertTriangle, XCircle, Maximize2 } from 'lucide-react';
import { io as socketIO } from 'socket.io-client';
import { socketAuth } from '../../../../packages/shared/socket';
import AdminHeader from '../components/AdminHeader/AdminHeader';
import API_ENDPOINTS from '../config/api';
import ExportDrawerTab from '../components/ActivityLog/ExportDrawerTab';
import LogDetailModal from '../components/ActivityLog/LogDetailModal';

const Page = styled.div`
  min-height: 100vh;
  background: #0a0a0a;
  color: #fff;
`;

const Content = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 32px 24px;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: ${p => p.$active ? 'rgba(0,153,255,0.2)' : 'rgba(255,255,255,0.06)'};
  border: 1px solid ${p => p.$active ? 'rgba(0,153,255,0.4)' : '#333'};
  border-radius: 8px;
  color: ${p => p.$active ? '#4a9eff' : '#aaa'};
  font-size: 13px;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { background: rgba(255,255,255,0.1); color: #fff; }

  svg.spinning { animation: spin 1s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }
`;

// ─── Feed items ─────────────────────────────────────────────────────

const FeedList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const FeedRow = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  background: ${p => p.$expanded ? 'rgba(255,255,255,0.04)' : 'transparent'};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
  color: #fff;
  width: 100%;
  transition: background 0.1s;
  &:hover { background: rgba(255,255,255,0.05); }
`;

const StatusDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${p => {
    if (p.$code >= 500) return '#ff4444';
    if (p.$code >= 400) return '#ffaa00';
    return '#00cc66';
  }};
`;

const FeedSummary = styled.span`
  flex: 1;
  font-size: 14px;
  color: #ddd;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FeedTime = styled.span`
  font-size: 12px;
  color: #555;
  font-family: monospace;
  flex-shrink: 0;
`;

const ExpandIcon = styled.div`
  color: #444;
  flex-shrink: 0;
  transition: transform 0.15s;
  transform: ${p => p.$expanded ? 'rotate(90deg)' : 'rotate(0deg)'};
`;

const DetailPanel = styled.div`
  margin: 0 0 4px 20px;
  padding: 10px 16px;
  background: rgba(255,255,255,0.03);
  border-left: 2px solid rgba(255,255,255,0.08);
  border-radius: 0 8px 8px 0;
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 100px;
`;

const DetailLabel = styled.span`
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #555;
`;

const DetailValue = styled.span`
  font-size: 13px;
  color: ${p => p.$color || '#aaa'};
  font-family: ${p => p.$mono ? 'monospace' : 'inherit'};
  word-break: break-all;
`;

const MethodBadge = styled.span`
  display: inline-block;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  font-family: monospace;
  background: ${p => {
    switch (p.$method) {
      case 'POST': return 'rgba(74,158,255,0.15)';
      case 'PUT': return 'rgba(255,170,0,0.15)';
      case 'PATCH': return 'rgba(0,200,180,0.15)';
      case 'DELETE': return 'rgba(255,68,68,0.15)';
      default: return 'rgba(255,255,255,0.1)';
    }
  }};
  color: ${p => {
    switch (p.$method) {
      case 'POST': return '#4a9eff';
      case 'PUT': return '#ffaa00';
      case 'PATCH': return '#00c8b4';
      case 'DELETE': return '#ff4444';
      default: return '#888';
    }
  }};
`;

const DateSeparator = styled.div`
  padding: 16px 0 8px;
  font-size: 12px;
  font-weight: 600;
  color: #555;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid rgba(255,255,255,0.06);
  margin-bottom: 4px;
`;

const LoadMore = styled.button`
  display: block;
  margin: 24px auto 0;
  padding: 10px 32px;
  background: rgba(255,255,255,0.06);
  border: 1px solid #333;
  border-radius: 8px;
  color: #aaa;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { background: rgba(255,255,255,0.1); color: #fff; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 64px 24px;
  color: #555;
  font-size: 15px;
`;

const TotalCount = styled.span`
  font-size: 12px;
  color: #555;
  margin-left: 12px;
  font-weight: 400;
`;

// ─── Helpers ────────────────────────────────────────────────────────

function formatTime(ts) {
  return new Date(ts).toLocaleTimeString('fi-FI', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function getDateKey(ts) {
  return new Date(ts).toLocaleDateString('fi-FI', { weekday: 'long', day: 'numeric', month: 'long' });
}

function isToday(ts) {
  return new Date(ts).toDateString() === new Date().toDateString();
}

function formatDuration(ms) {
  if (ms == null) return '-';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function durationColor(ms) {
  if (ms > 5000) return '#ff4444';
  if (ms > 1000) return '#ffaa00';
  return '#666';
}

function statusLabel(code) {
  if (code >= 500) return 'Virhe';
  if (code === 404) return 'Ei löytynyt';
  if (code >= 400) return 'Hylätty';
  if (code === 201) return 'Luotu';
  return 'OK';
}

// ─── Component ──────────────────────────────────────────────────────

export default function ActivityLogScreen() {
  const { instanceId: urlInstanceId } = useAdminInstance();

  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [instanceName, setInstanceName] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [detailEntry, setDetailEntry] = useState(null);
  const [allInstances, setAllInstances] = useState(!urlInstanceId);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [deviceFilter, setDeviceFilter] = useState('');
  const intervalRef = useRef(null);

  const activeInstanceId = allInstances ? null : urlInstanceId;

  useEffect(() => {
    if (!urlInstanceId) return;
    fetch(API_ENDPOINTS.INSTANCE(urlInstanceId))
      .then(r => r.json())
      .then(data => setInstanceName(data.title || data.name || ''))
      .catch(() => {});
  }, [urlInstanceId]);

  const fetchLog = useCallback(async (offset = 0, append = false) => {
    setLoading(true);
    try {
      const url = new URL(API_ENDPOINTS.ACTION_LOG_UNIFIED());
      url.searchParams.set('limit', '100');
      url.searchParams.set('offset', String(offset));
      if (activeInstanceId) url.searchParams.set('instanceId', activeInstanceId);
      if (categoryFilter) url.searchParams.set('category', categoryFilter);
      if (deviceFilter) url.searchParams.set('deviceId', deviceFilter);

      const res = await fetch(url.toString(), { credentials: 'include' });
      const data = await res.json();

      if (append) {
        setEntries(prev => [...prev, ...data.entries]);
      } else {
        setEntries(data.entries);
      }
      setTotal(data.total);
    } catch (err) {
      console.error('[ActivityLog] Fetch failed:', err);
    } finally {
      setLoading(false);
    }
  }, [activeInstanceId, categoryFilter, deviceFilter]);

  useEffect(() => { fetchLog(); }, [fetchLog]);

  // Real-time log via socket (replaces polling)
  useEffect(() => {
    if (!autoRefresh) return;

    const socketUrl = import.meta.env.VITE_SOCKET_URL || 'http://localhost:3010';
    // join-admin-log takes an admin sign-in, carried by the ticket (#1226)
    const socket = socketIO(socketUrl, { transports: ['websocket'], auth: socketAuth() });

    socket.on('connect', () => {
      socket.emit('join-admin-log');
    });

    socket.on('log-entry', (entry) => {
      // Apply client-side filters
      if (categoryFilter && entry.category !== categoryFilter) {
        if (categoryFilter === 'error' && entry.status_code < 400 && entry.success !== false) return;
        if (categoryFilter !== 'error' && entry.category !== categoryFilter) return;
      }
      if (deviceFilter && entry.device_id !== deviceFilter) return;
      if (activeInstanceId && entry.instance_id !== activeInstanceId) return;

      setEntries(prev => [entry, ...prev].slice(0, 500));
      setTotal(prev => prev + 1);
    });

    return () => {
      socket.emit('leave-admin-log');
      socket.disconnect();
    };
  }, [autoRefresh, categoryFilter, deviceFilter, activeInstanceId]);

  // Group entries by date
  const groupedEntries = [];
  let lastDateKey = null;
  for (const e of entries) {
    const dk = getDateKey(e.timestamp);
    if (dk !== lastDateKey) {
      groupedEntries.push({ type: 'date', label: isToday(e.timestamp) ? 'Tänään' : dk, key: dk });
      lastDateKey = dk;
    }
    groupedEntries.push({ type: 'entry', data: e, key: e.id });
  }

  return (
    <Page>
      <AdminHeader instanceId={urlInstanceId} instanceName={instanceName} />
      <ExportDrawerTab
        entries={entries}
        total={total}
        activeInstanceId={activeInstanceId}
        categoryFilter={categoryFilter}
        deviceFilter={deviceFilter}
      />
      <Content>
        <TopBar>
          <Title>
            Toimintaloki
            {total > 0 && <TotalCount>({total})</TotalCount>}
          </Title>
          <Controls>
            {urlInstanceId && (
              <Button $active={allInstances} onClick={() => setAllInstances(v => !v)}>
                {allInstances ? 'Kaikki instanssit' : instanceName || 'Tämä instanssi'}
              </Button>
            )}
            <Button onClick={() => fetchLog()} disabled={loading}>
              <RefreshCw size={14} className={loading ? 'spinning' : ''} />
              Päivitä
            </Button>
            <Button $active={autoRefresh} onClick={() => setAutoRefresh(v => !v)}>
              Auto {autoRefresh ? 'ON' : 'OFF'}
            </Button>
          </Controls>
        </TopBar>

        <div style={{ display: 'flex', gap: 8, padding: '0 0 12px', flexWrap: 'wrap' }}>
          {['', 'user_action', 'ai_request', 'socket', 'error'].map(cat => (
            <Button
              key={cat || 'all'}
              $active={categoryFilter === cat}
              onClick={() => setCategoryFilter(cat)}
              style={{ fontSize: 12, padding: '4px 10px' }}
            >
              {cat === '' ? 'Kaikki' : cat === 'user_action' ? 'Käyttäjä' : cat === 'ai_request' ? 'AI' : cat === 'socket' ? 'Socket' : 'Virheet'}
            </Button>
          ))}
          {deviceFilter && (
            <Button onClick={() => setDeviceFilter('')} style={{ fontSize: 12, padding: '4px 10px', color: '#c084fc' }}>
              Laite: {deviceFilter.substring(0, 8)}... ✕
            </Button>
          )}
        </div>

        {entries.length === 0 && !loading ? (
          <EmptyState>Ei lokimerkintöjä</EmptyState>
        ) : (
          <>
            <FeedList>
              {groupedEntries.map(item => {
                if (item.type === 'date') {
                  return <DateSeparator key={item.key}>{item.label}</DateSeparator>;
                }

                const e = item.data;
                const expanded = expandedId === e.id;

                return (
                  <React.Fragment key={e.id}>
                    <FeedRow
                      $expanded={expanded}
                      onClick={() => setExpandedId(expanded ? null : e.id)}
                    >
                      <StatusDot $code={e.category === 'ai_request' ? (e.success === false ? 500 : 200) : e.status_code} />
                      {e.category === 'ai_request' && <span style={{ color: '#c084fc', fontSize: 11, marginRight: 4 }}>AI</span>}
                      {e.category === 'error' && <span style={{ color: '#ef4444', fontSize: 11, marginRight: 4 }}>ERR</span>}
                      <FeedSummary>{e.summary}</FeedSummary>
                      <FeedTime>{formatTime(e.timestamp)}</FeedTime>
                      <ExpandIcon $expanded={expanded}>
                        <ChevronRight size={14} />
                      </ExpandIcon>
                    </FeedRow>

                    {expanded && (
                      <DetailPanel>
                        <DetailItem>
                          <DetailLabel>Metodi</DetailLabel>
                          <DetailValue><MethodBadge $method={e.method}>{e.method}</MethodBadge></DetailValue>
                        </DetailItem>
                        <DetailItem>
                          <DetailLabel>Polku</DetailLabel>
                          <DetailValue $mono $color="#888">{e.path}</DetailValue>
                        </DetailItem>
                        <DetailItem>
                          <DetailLabel>Tila</DetailLabel>
                          <DetailValue $color={e.status_code >= 400 ? '#ffaa00' : '#aaa'}>
                            {e.status_code} {statusLabel(e.status_code)}
                          </DetailValue>
                        </DetailItem>
                        <DetailItem>
                          <DetailLabel>Kesto</DetailLabel>
                          <DetailValue $mono $color={durationColor(e.duration_ms)}>
                            {formatDuration(e.duration_ms)}
                          </DetailValue>
                        </DetailItem>
                        {e.device_id && (
                          <DetailItem>
                            <DetailLabel>Laite</DetailLabel>
                            <DetailValue
                              $mono $color="#8ab4f8"
                              style={{ cursor: 'pointer', textDecoration: 'underline' }}
                              onClick={(ev) => { ev.stopPropagation(); setDeviceFilter(e.device_id); }}
                            >
                              {e.device_id}
                            </DetailValue>
                          </DetailItem>
                        )}
                        {e.ai_model && (
                          <DetailItem>
                            <DetailLabel>AI-malli</DetailLabel>
                            <DetailValue $mono $color="#c084fc">{e.ai_model}</DetailValue>
                          </DetailItem>
                        )}
                        {e.tokens_input != null && (
                          <DetailItem>
                            <DetailLabel>Tokenit</DetailLabel>
                            <DetailValue $mono>{e.tokens_input} in + {e.tokens_output} out</DetailValue>
                          </DetailItem>
                        )}
                        {e.estimated_cost_usd != null && (
                          <DetailItem>
                            <DetailLabel>Hinta</DetailLabel>
                            <DetailValue $mono $color="#22c55e">${e.estimated_cost_usd.toFixed(4)}</DetailValue>
                          </DetailItem>
                        )}
                        {e.rag_results_count > 0 && (
                          <DetailItem>
                            <DetailLabel>RAG-lähteet</DetailLabel>
                            <DetailValue $mono>{e.rag_results_count} tulosta</DetailValue>
                          </DetailItem>
                        )}
                        {e.error_message && (
                          <DetailItem>
                            <DetailLabel>Virhe</DetailLabel>
                            <DetailValue $color="#ef4444">{e.error_message}</DetailValue>
                          </DetailItem>
                        )}
                        <DetailItem>
                          <DetailLabel>IP</DetailLabel>
                          <DetailValue $mono $color="#666">{e.actor}</DetailValue>
                        </DetailItem>
                        {e.instance_id && (
                          <DetailItem>
                            <DetailLabel>Instanssi</DetailLabel>
                            <DetailValue $mono $color="#666">{e.instance_id.substring(0, 8)}...</DetailValue>
                          </DetailItem>
                        )}
                        <DetailItem style={{ marginLeft: 'auto', alignSelf: 'flex-end' }}>
                          <button
                            onClick={(ev) => { ev.stopPropagation(); setDetailEntry(e); }}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 6,
                              padding: '6px 12px',
                              background: 'rgba(74,158,255,0.12)',
                              border: '1px solid rgba(74,158,255,0.35)',
                              borderRadius: 6, color: '#8ab4f8',
                              fontSize: 12, cursor: 'pointer',
                            }}
                          >
                            <Maximize2 size={12} />
                            Näytä koko data
                          </button>
                        </DetailItem>
                      </DetailPanel>
                    )}
                  </React.Fragment>
                );
              })}
            </FeedList>

            {entries.length < total && (
              <LoadMore onClick={() => fetchLog(entries.length, true)} disabled={loading}>
                <ChevronDown size={14} style={{ marginRight: 6 }} />
                Lataa lisää ({total - entries.length} jäljellä)
              </LoadMore>
            )}
          </>
        )}
      </Content>
      {detailEntry && (
        <LogDetailModal entry={detailEntry} onClose={() => setDetailEntry(null)} />
      )}
    </Page>
  );
}
